/* eslint-disable import/order */
import {
  AnyAction,
  autoBatchEnhancer,
  configureStore,
  Middleware,
  ThunkAction
} from '@reduxjs/toolkit'
import {priceInsightsApi} from 'components/PriceTrend/services/priceInsightsApi'
import accommodation from 'modules/accommodation/slice'
import analytics, {setOfferViewed} from 'modules/analytics/slice'
import {appRatingsApi} from 'modules/appRatings'
import bookingManagement from 'modules/bookingManagement/slice'
import {chatbotApi} from 'modules/chatBot/chatBotApi'
import cookies from 'modules/cookies/slice'
import {hotelMappingApi} from 'modules/hotelMapping'
import marketingNotifications from 'modules/marketingNotifications/slice'
import meta, {augmentMetaState} from 'modules/meta'
import overlay from 'modules/overlay/slice'
import popupOnExit from 'modules/popupOnExit/slice'
import priceAlertPrompt from 'modules/priceAlertPrompt/slice'
import progressBar from 'modules/progressBar/slice'
import sapiSearch from 'modules/sapiSearch/slice'
import {augmentSearchState} from 'modules/search'
import search from 'modules/search/slice'
import searchBox from 'modules/searchBox/slice'
import {seoApi} from 'modules/seoApi'
import {translateInsightsApi} from 'modules/translateInsightsApi'
import {translateReviewsApi} from 'modules/translateReviewsApi'
import {translationApi} from 'modules/translationApi'
import {combineReducers} from 'redux'
import {Toggle} from 'types/Toggle'

import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import authentication from '@vio/core/src/auth/modules/slice'
import {bookApi} from '@vio/core/src/booking/services/bookApi'
import {bookingRetrieveApi} from '@vio/core/src/booking/services/bookingRetrieveApi'
import {otpApi} from '@vio/core/src/booking/services/otpApi'
import experiments from '@vio/core/src/experiments/modules/slice'
import {localizationApi} from '@vio/core/src/localization/business/getCountriesList'
import {offerCheckApi} from '@vio/core/src/offer/services/offerCheckApi'
import {offerConfigurationApi} from '@vio/core/src/offer/services/offerConfigurationApi'
import {paymentSettingsApi} from '@vio/core/src/offer/services/paymentSettingsApi'
import {
  searchApi,
  SearchApiThunkExtra
} from '@vio/core/src/sapi/services/searchApi'
import colorPriceCalendar from '@vio/core/src/search/modules/ColorPriceCalendar/slice'
import {trustpilotApi} from '@vio/core/src/trustpilotWidget/services/trustpilotApi'
import emailSignUp from '@vio/shared/src/authentication/Email/slice'
import dealFreeze from '@vio/shared/src/dealFreeze/modules/slice'
import {dealFreezeApi} from '@vio/shared/src/dealFreeze/services/dealFreezeApi'
import {priceWatchApi} from '@vio/shared/src/priceWatch/services'
import priceWatch from '@vio/shared/src/priceWatch/slice'
import {notificationPreferencesApi} from '@vio/shared/src/user/notificationPreferences/modules'

import appMiddlewareList from '../middleware/middlewareList'
import {chatSessionPersistenceMiddleware} from '../modules/vioMcpChat/modules/middleware'
import chat from '../modules/vioMcpChat/modules/slice'
import {chatApi} from '../modules/vioMcpChat/services/chatApi'

const middlewareList: Middleware[] = [
  searchApi.middleware,
  dealFreezeApi.middleware,
  bookApi.middleware,
  bookingRetrieveApi.middleware,
  offerConfigurationApi.middleware,
  trustpilotApi.middleware,
  priceWatchApi.middleware,
  appRatingsApi.middleware,
  offerCheckApi.middleware,
  paymentSettingsApi.middleware,
  notificationPreferencesApi.middleware,
  chatApi.middleware,
  chatSessionPersistenceMiddleware,
  chatbotApi.middleware,
  hotelMappingApi.middleware,
  localizationApi.middleware,
  priceInsightsApi.middleware,
  seoApi.middleware,
  translateInsightsApi.middleware,
  translateReviewsApi.middleware,
  translationApi.middleware,
  otpApi.middleware,
  ...appMiddlewareList
]

const rootReducer = combineReducers({
  meta,
  overlay,
  marketingNotifications,
  searchBox,
  search,
  progressBar,
  popupOnExit,
  priceAlertPrompt,
  cookies,
  authentication,
  chat,
  dealFreeze,
  experiments,
  sapiSearch,
  analytics,
  accommodation,
  bookingManagement,
  priceWatch,
  emailSignUp,
  [searchApi.reducerPath]: searchApi.reducer,
  [dealFreezeApi.reducerPath]: dealFreezeApi.reducer,
  [bookApi.reducerPath]: bookApi.reducer,
  [bookingRetrieveApi.reducerPath]: bookingRetrieveApi.reducer,
  [offerConfigurationApi.reducerPath]: offerConfigurationApi.reducer,
  [trustpilotApi.reducerPath]: trustpilotApi.reducer,
  [priceWatchApi.reducerPath]: priceWatchApi.reducer,
  [appRatingsApi.reducerPath]: appRatingsApi.reducer,
  [offerCheckApi.reducerPath]: offerCheckApi.reducer,
  [paymentSettingsApi.reducerPath]: paymentSettingsApi.reducer,
  [notificationPreferencesApi.reducerPath]: notificationPreferencesApi.reducer,
  [chatApi.reducerPath]: chatApi.reducer,
  [chatbotApi.reducerPath]: chatbotApi.reducer,
  [hotelMappingApi.reducerPath]: hotelMappingApi.reducer,
  [localizationApi.reducerPath]: localizationApi.reducer,
  [priceInsightsApi.reducerPath]: priceInsightsApi.reducer,
  [seoApi.reducerPath]: seoApi.reducer,
  [translateInsightsApi.reducerPath]: translateInsightsApi.reducer,
  [translateReviewsApi.reducerPath]: translateReviewsApi.reducer,
  [translationApi.reducerPath]: translationApi.reducer,
  [otpApi.reducerPath]: otpApi.reducer,
  colorPriceCalendar
})

export type RootState = ReturnType<typeof rootReducer>

export interface ThunkExtra extends SearchApiThunkExtra {
  toggle: Toggle
}

const devToolsMaxAge =
  process.env.NEXT_PUBLIC_REDUX_DEVTOOLS_MAXAGE !== undefined
    ? parseInt(process.env.NEXT_PUBLIC_REDUX_DEVTOOLS_MAXAGE)
    : 50

/**
 * Initialize a store with optional initialState
 */
export const initializeStore = (
  initialState: Partial<RootState> = {},
  extraArgument: ThunkExtra
) =>
  configureStore({
    devTools: {
      // These tracking events are so noisy they make redux devtools unusable.
      actionsDenylist: [trackEvent.type, setOfferViewed.type],
      maxAge: devToolsMaxAge
    },
    reducer: rootReducer,
    preloadedState: {
      ...initialState,
      meta:
        typeof window !== 'undefined'
          ? augmentMetaState(initialState)
          : initialState.meta,
      search:
        typeof window !== 'undefined'
          ? augmentSearchState(initialState)
          : initialState.search
    },
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware({
        thunk: {
          extraArgument
        },
        serializableCheck: false,
        immutableCheck: false
      }).concat(...middlewareList),
    enhancers: existingEnhancers => {
      // Add the autobatch enhancer to the store setup
      return existingEnhancers.concat(autoBatchEnhancer())
    }
  })

export type Store = ReturnType<typeof initializeStore>

/**
 * Use this for thunks (eg. async actions)
 * ReturnType is optional
 */
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  ThunkExtra,
  AnyAction
>
